// Cross-origin API bridge for the packaged (Capacitor) app.
//
// In the browser this module is inert: apiBase is "" (see src/config.js), so
// installNativeApiBridge() returns immediately and nothing is patched.
//
// The iOS app bundles this SPA and loads it from capacitor://localhost — a
// different origin from the Netlify functions. A relative fetch("/api/library")
// there would resolve against the app bundle and 404. This bridge rewrites only
// paths that begin with /api/ to the absolute origin and leaves every other
// request alone (host CDNs, embed iframes, TMDB in direct mode).
//
// Why one interception point instead of editing every call site: the app reaches
// its API from ~30 places spread across screens, and missing one fails SILENTLY
// in the packaged app only, because this whole file is a no-op in the browser
// where the rest of the app is developed. One choke point cannot be forgotten.
//
// Media URLs that bypass fetch entirely — an HLS playlist handed to hls.js or
// assigned to <video src>, an <iframe src> for our own /embed/ proxy — are
// invisible to this bridge, so those are absolutised with apiUrl() where they
// are constructed (see playback/providers.js, screens/LiveTVScreen.jsx).
import { apiBase } from "./config.js";

const isApiPath = (url) => typeof url === "string" && url.startsWith("/api/");

export function installNativeApiBridge() {
  if (!apiBase || typeof window === "undefined") return;

  const absolute = (url) => (isApiPath(url) ? `${apiBase}${url}` : url);

  // fetch — as a string, a URL object, or a Request (a Request has already
  // resolved its relative path against the app origin by the time we see it).
  const nativeFetch = window.fetch.bind(window);
  window.fetch = (input, init) => {
    try {
      if (isApiPath(input)) return nativeFetch(absolute(input), init);
      if (input instanceof URL && isApiPath(input.pathname)) {
        return nativeFetch(new URL(`${apiBase}${input.pathname}${input.search}`), init);
      }
      if (typeof Request !== "undefined" && input instanceof Request) {
        const parsed = new URL(input.url);
        if (parsed.origin === location.origin && isApiPath(parsed.pathname)) {
          return nativeFetch(new Request(`${apiBase}${parsed.pathname}${parsed.search}`, input));
        }
      }
    } catch {
      /* fall through and issue the request untouched */
    }
    return nativeFetch(input, init);
  };

  // XMLHttpRequest — hls.js loads playlists this way. Covered so a playlist URL
  // that escaped apiUrl() still resolves instead of silently failing.
  const nativeOpen = XMLHttpRequest.prototype.open;
  XMLHttpRequest.prototype.open = function (method, url, ...rest) {
    return nativeOpen.call(this, method, absolute(url), ...rest);
  };
}
